/**
 * DashboardSidebarV2 — social-first labelled navigation.
 *
 * Doctrine source: docs/design/doctrine.md
 *
 * Behaviour:
 *   - Studio plus five familiar social destinations stay directly visible.
 *   - Creation tools and lower-frequency product areas use labelled flyout menus.
 *   - Active items get an accent stripe and tint.
 *   - Profile avatar at the bottom has its tooltip showing both display name + handle
 *     (the only item where that distinction matters at a glance).
 *
 * RTL: every position uses logical properties (`inset-inline-start`,
 * `padding-inline`), so this component flips correctly when
 * `documentElement.dir === 'rtl'` with no React-side changes.
 *
 * Gated by useDesignV2() inside DashboardLayout; this file is never
 * rendered when the flag is off.
 */
import {
  BarChart3,
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  Library,
  MoreHorizontal,
  PanelsTopLeft,
  PenLine,
  PersonStanding,
  Plus,
  Users,
  Video,
  Image as ImageIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import Link from '@/components/LocaleLink';
import { Avatar, Badge, Menu } from '@/design/primitives';
import { useLocaleNavigate } from '@/hooks/useLocaleNavigate';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { listSessions, switchSession, removeSession, getActiveUserId } from '@/utils/secureToken';
import { clearToken } from '@/utils/secureToken';
import {
  buildDashboardNavGroups,
  buildPrimaryDashboardNav,
  type DashboardNavEntry,
  type DashboardNavGroup,
} from './dashboardNavigation';
import './sidebar-v2.css';

interface User {
  id: string;
  email: string;
  username?: string | null;
  avatar_url?: string | null;
  display_name?: string | null;
}

interface Props {
  user: User | null;
  unreadMessages?: number;
  unreadNotifications?: number;
  compact?: boolean;
  onLogout: () => void;
}

export default function DashboardSidebarV2({
  user,
  unreadMessages = 0,
  unreadNotifications = 0,
  compact = false,
  onLogout,
}: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useLocaleNavigate();
  const stage3dEnabled = useFeatureFlag('stage3d');
  const [meMenuOpen, setMeMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<DashboardNavGroup['id'] | null>(null);
  const meWrapRef = useRef<HTMLDivElement>(null);
  const groupWrapRefs = useRef<Partial<Record<DashboardNavGroup['id'], HTMLDivElement | null>>>({});

  const primary = buildPrimaryDashboardNav(t, unreadMessages, unreadNotifications);
  const groups = buildDashboardNavGroups(t);
  const createTools = groups
    .find(group => group.id === 'create')
    ?.items.filter(item => item.href !== '/dashboard/write') ?? [];
  const utilityHrefs = new Set([
    '/dashboard/addons',
    '/dashboard/my-plugins',
    '/dashboard/developer',
    '/dashboard/credits',
    '/dashboard/pricing',
    '/dashboard/security',
    '/dashboard/settings',
  ]);
  const utilities = groups
    .find(group => group.id === 'tools')
    ?.items.filter(item => utilityHrefs.has(item.href)) ?? [];
  const sidebarGroups = groups
    .filter(group => group.id !== 'create')
    .map(group => group.id === 'tools'
      ? { ...group, items: group.items.filter(item => !utilityHrefs.has(item.href)) }
      : group);
  const groupIcons = {
    content: Library,
    create: Plus,
    community: Users,
    business: BriefcaseBusiness,
    insights: BarChart3,
    tools: PanelsTopLeft,
  } as const;

  // useLocation().pathname includes the locale prefix (/en/dashboard/...),
  // so strip it before comparing against bare hrefs like /dashboard.
  const bare = pathname.replace(/^\/[a-z]{2}(-[A-Za-z]{2})?(?=\/|$)/, '') || '/';
  const isActive = (href: string) => {
    if (href === '/dashboard') return bare === '/dashboard';
    if (href === '/dashboard/chats' && bare.startsWith('/dashboard/messages')) return true;
    if (bare === href) return true;

    // Prefer a configured child destination over its parent. Without this,
    // /library and /library/tags (or /outfits and /outfits/analytics) both
    // appear active at the same time.
    const hasMoreSpecificMatch = [...primary, ...groups.flatMap(group => group.items)]
      .some(item => item.href !== href
        && item.href.startsWith(href + '/')
        && (bare === item.href || bare.startsWith(item.href + '/')));
    return !hasMoreSpecificMatch && bare.startsWith(href + '/');
  };

  const closeFlyouts = () => {
    setCreateMenuOpen(false);
    setMoreMenuOpen(false);
    setOpenGroupId(null);
  };

  const goTo = (href: string) => {
    closeFlyouts();
    navigate(href);
  };

  const openComposer = () => {
    closeFlyouts();
    window.dispatchEvent(new CustomEvent('soltik:compose'));
  };

  useEffect(() => {
    setCreateMenuOpen(false);
    setMoreMenuOpen(false);
    setOpenGroupId(null);
  }, [pathname]);

  // Group flyouts use fixed positioning so they can escape the sidebar's
  // overflow clip. Anchor the open flyout to its actual trigger, then shift
  // it only as much as needed to stay inside the viewport. This keeps lower
  // sections such as Workspace visually connected instead of pinning every
  // menu to the same unrelated spot near the top of the screen.
  useLayoutEffect(() => {
    if (!openGroupId) return;

    const positionMenu = () => {
      const wrapper = groupWrapRefs.current[openGroupId];
      const trigger = wrapper?.querySelector<HTMLElement>(':scope > .sb2__section-trigger');
      const menu = wrapper?.querySelector<HTMLElement>(':scope > .sb2__group-menu');
      if (!trigger || !menu) return;

      const viewportPadding = 12;
      const triggerTop = trigger.getBoundingClientRect().top;
      const menuHeight = menu.getBoundingClientRect().height;
      const highestAllowedTop = Math.max(
        viewportPadding,
        window.innerHeight - viewportPadding - menuHeight,
      );
      const nextTop = Math.max(viewportPadding, Math.min(triggerTop, highestAllowedTop));
      menu.style.insetBlockStart = `${nextTop}px`;
    };

    positionMenu();
    const sidebar = groupWrapRefs.current[openGroupId]?.closest('.sb2');
    let animationFrame: number | null = null;
    const queuePosition = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        positionMenu();
      });
    };
    window.addEventListener('resize', queuePosition);
    sidebar?.addEventListener('scroll', queuePosition, { passive: true });
    return () => {
      window.removeEventListener('resize', queuePosition);
      sidebar?.removeEventListener('scroll', queuePosition);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [openGroupId]);

  // Close the account menu on outside click handled by Menu primitive itself.
  // Re-fetch sessions whenever the menu opens so newly-added accounts show.
  const [sessions, setSessions] = useState<ReturnType<typeof listSessions>>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!meMenuOpen) return;
    setSessions(listSessions());
    setActiveId(getActiveUserId());
  }, [meMenuOpen]);

  const meName = user?.display_name || user?.username || t('common.account', 'Account');
  // Keep account email inside the intentional account menu. Persistently
  // showing it in the sidebar leaks PII during screenshots and screen shares.
  const meHandle = user?.username ? `@${user.username}` : '';
  const meTooltip = `${meName}${user?.username ? ` · @${user.username}` : ''}`;

  const handleSwitch = (userId: string) => {
    if (userId === activeId) return setMeMenuOpen(false);
    const target = sessions.find(s => s.userId === userId);
    if (switchSession(userId) && target) {
      window.dispatchEvent(new CustomEvent('soltik-web-auth-change', {
        detail: { token: target.token, email: target.email || '' },
      }));
      window.location.href = '/en/dashboard';
    }
  };

  const handleRemoveSession = (userId: string) => {
    removeSession(userId);
    const remaining = listSessions();
    if (userId === activeId) {
      const next = remaining[0];
      window.dispatchEvent(new CustomEvent('soltik-web-auth-change', {
        detail: next ? { token: next.token, email: next.email || '' } : {},
      }));
      window.location.href = next ? '/en/dashboard' : '/en/login';
    } else {
      setSessions(remaining);
    }
  };

  const handleAddAccount = () => {
    window.location.href = '/en/login?add=1';
  };

  const handleLogoutClicked = () => {
    setMeMenuOpen(false);
    clearToken();
    onLogout();
  };

  return (
    <nav className={`sb2 ${compact ? 'sb2--compact' : ''}`} aria-label={t('nav.primary', 'Primary navigation')}>
      <Link to="/dashboard" className="sb2__logo" aria-label="Soltik">
        <img src="/logo-yellow.png" alt="" />
        <span>Soltik</span>
      </Link>

      <div className="sb2__flyout-wrap sb2__create-wrap">
        <button
          type="button"
          className="sb2__item sb2__item--accent"
          aria-label={t('common.create', 'Create') as string}
          aria-haspopup="menu"
          aria-expanded={createMenuOpen}
          title={compact ? t('common.create', 'Create') as string : undefined}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => { setCreateMenuOpen(v => !v); setMoreMenuOpen(false); setOpenGroupId(null); setMeMenuOpen(false); }}
        >
          <Plus size={20} aria-hidden="true" />
          <span className="sb2__label">{t('common.create', 'Create')}</span>
        </button>
        <Menu open={createMenuOpen} onClose={() => setCreateMenuOpen(false)} className="sb2__create-menu">
          <Menu.Heading>{t('common.create', 'Create')}</Menu.Heading>
          <div className="sb2__create-actions">
            <Menu.Item onSelect={openComposer} icon={<Plus size={17} />}>
              {t('nav.newPost', 'New post')}
            </Menu.Item>
            <Menu.Item onSelect={() => goTo('/dashboard/studio?page=canvas')} icon={<ImageIcon size={17} />}>
              {t('homeNext.makeImage', 'Image')}
            </Menu.Item>
            {stage3dEnabled && (
              <Menu.Item
                onSelect={() => goTo('/dashboard/studio?page=stage3d')}
                icon={<PersonStanding size={17} />}
              >
                {t('homeNext.make3dStage', '3D Stage')}
              </Menu.Item>
            )}
            <Menu.Item onSelect={() => goTo('/dashboard/studio?page=story')} icon={<Video size={17} />}>
              {t('homeNext.makeVideo', 'Video')}
            </Menu.Item>
            <Menu.Item onSelect={() => goTo('/dashboard/write')} icon={<PenLine size={17} />}>
              {t('homeNext.makeWrite', 'Write')}
            </Menu.Item>
          </div>
          <Menu.Separator />
          <div className="sb2__create-tools">
            {createTools.map(({ href, label, Icon }) => (
              <Menu.Item
                key={href}
                onSelect={() => goTo(href)}
                icon={<Icon size={17} />}
                className={isActive(href) ? 'sb2__menu-item--active' : undefined}
              >
                {label}
              </Menu.Item>
            ))}
          </div>
        </Menu>
      </div>

      <div className="sb2__divider" />

      {primary.map(({ href, label, Icon, badge }) => (
        <RailItem
          key={href}
          href={href}
          label={label}
          Icon={Icon}
          active={isActive(href)}
          badge={badge}
        />
      ))}

      <div className="sb2__divider" />

      {sidebarGroups.map(group => {
        const GroupIcon = groupIcons[group.id];
        const isOpen = openGroupId === group.id;
        const containsActivePage = group.items.some(item => isActive(item.href));
        const wide = group.items.length > 10;

        return (
          <div
            key={group.id}
            ref={(element) => { groupWrapRefs.current[group.id] = element; }}
            className="sb2__flyout-wrap sb2__group-wrap"
          >
            <button
              type="button"
              className={`sb2__item sb2__section-trigger ${containsActivePage ? 'sb2__item--active' : ''}`}
              aria-label={group.label}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              title={compact ? group.label : undefined}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => {
                setOpenGroupId(current => current === group.id ? null : group.id);
                setCreateMenuOpen(false);
                setMoreMenuOpen(false);
                setMeMenuOpen(false);
              }}
            >
              <GroupIcon size={19} aria-hidden="true" />
              <span className="sb2__label">{group.label}</span>
              <ChevronRight
                size={14}
                className={`sb2__section-chevron ${isOpen ? 'sb2__section-chevron--open' : ''}`}
                aria-hidden="true"
              />
            </button>
            <Menu
              open={isOpen}
              onClose={() => setOpenGroupId(null)}
              className={`sb2__group-menu ${wide ? 'sb2__group-menu--wide' : ''}`}
            >
              <Menu.Heading>{group.label}</Menu.Heading>
              <div className="sb2__group-items">
                {group.items.map(({ href, label, Icon }) => (
                  <Menu.Item
                    key={href}
                    onSelect={() => goTo(href)}
                    icon={<Icon size={17} />}
                    className={isActive(href) ? 'sb2__menu-item--active' : undefined}
                  >
                    {label}
                  </Menu.Item>
                ))}
              </div>
            </Menu>
          </div>
        );
      })}

      <div className="sb2__spacer" />

      <div className="sb2__flyout-wrap sb2__more-wrap">
        <button
          type="button"
          className={`sb2__item ${utilities.some(item => isActive(item.href)) ? 'sb2__item--active' : ''}`}
          aria-label={t('common.more', 'More') as string}
          aria-haspopup="menu"
          aria-expanded={moreMenuOpen}
          title={compact ? t('common.more', 'More') as string : undefined}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => { setMoreMenuOpen(v => !v); setCreateMenuOpen(false); setOpenGroupId(null); setMeMenuOpen(false); }}
        >
          <MoreHorizontal size={20} aria-hidden="true" />
          <span className="sb2__label">{t('common.more', 'More')}</span>
        </button>
        <Menu open={moreMenuOpen} onClose={() => setMoreMenuOpen(false)} className="sb2__utility-menu">
          <Menu.Heading>{t('common.more', 'More')}</Menu.Heading>
          {utilities.map(({ href, label, Icon }) => (
            <Menu.Item
              key={href}
              onSelect={() => goTo(href)}
              icon={<Icon size={17} />}
              className={isActive(href) ? 'sb2__menu-item--active' : undefined}
            >
              {label}
            </Menu.Item>
          ))}
        </Menu>
      </div>

      {/* Account: clearly labelled avatar + account dropdown. */}
      <div className="sb2__me-wrap" ref={meWrapRef}>
        <button
          type="button"
          className="sb2__me"
          data-label={meTooltip}
          title={meTooltip}
          onClick={() => { setMeMenuOpen(v => !v); setCreateMenuOpen(false); setMoreMenuOpen(false); setOpenGroupId(null); }}
          aria-label={meName}
          aria-haspopup="menu"
          aria-expanded={meMenuOpen}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Avatar size="md" src={user?.avatar_url ?? undefined} name={meName} />
          <span className="sb2__me-copy">
            <span className="sb2__me-name">{meName}</span>
            {meHandle && <span className="sb2__me-handle">{meHandle}</span>}
          </span>
          <ChevronDown size={12} className="sb2__me-chevron" aria-hidden="true" />
        </button>

        <Menu open={meMenuOpen} onClose={() => setMeMenuOpen(false)} align="start">
          <Menu.Heading>{t('accounts.title', 'Accounts')}</Menu.Heading>
          {sessions.map(s => {
            const isMe = s.userId === activeId;
            const sessionName = s.displayName || s.username || s.email?.split('@')[0] || 'Account';
            return (
              <Menu.Item
                key={s.userId}
                onSelect={() => handleSwitch(s.userId)}
                icon={<Avatar size="sm" src={s.avatarUrl ?? undefined} name={sessionName} />}
              >
                <span className="sb2__menu-acc">
                  <span className="sb2__menu-acc-name">{sessionName}</span>
                  {s.username && <span className="sb2__menu-acc-handle">@{s.username}</span>}
                </span>
                {isMe && <span className="sb2__menu-acc-active" aria-hidden="true">●</span>}
                <button
                  type="button"
                  className="sb2__menu-acc-remove"
                  onClick={(e) => { e.stopPropagation(); handleRemoveSession(s.userId); }}
                  aria-label={t('common.remove', 'Remove')}
                >
                  ✕
                </button>
              </Menu.Item>
            );
          })}
          {sessions.length < 5 && (
            <Menu.Item onSelect={handleAddAccount} icon={<Plus size={16} />}>
              {t('accounts.add', 'Add account')}
            </Menu.Item>
          )}
          <Menu.Separator />
          {/* Quick path to the public profile page — settings → profile
              → preview is a long way around. The username on the
              account row would be the natural target but tapping
              that already triggers session-switch, so the menu
              item lives here instead. */}
          {user?.username && (
            <Menu.Item onSelect={() => { setMeMenuOpen(false); navigate(`/u/${user.username}`); }}>
              {t('profile.viewProfile', 'View my profile')}
            </Menu.Item>
          )}
          <Menu.Item onSelect={() => { setMeMenuOpen(false); navigate('/dashboard/settings'); }}>
            {t('common.settings', 'Settings')}
          </Menu.Item>
          <Menu.Item destructive onSelect={handleLogoutClicked}>
            {t('common.signOut', 'Sign out')}
          </Menu.Item>
        </Menu>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */

interface RailItemProps {
  href: string;
  label: string;
  Icon: DashboardNavEntry['Icon'];
  active?: boolean;
  badge?: number | 'dot';
  accent?: boolean;
}

function RailItem({ href, label, Icon, active, badge, accent }: RailItemProps) {
  const classes = [
    'sb2__item',
    active ? 'sb2__item--active' : '',
    accent ? 'sb2__item--accent' : '',
  ].filter(Boolean).join(' ');

  return (
    <Link to={href} className={classes} aria-label={label} title={label} aria-current={active ? 'page' : undefined}>
      <Icon size={20} aria-hidden="true" />
      <span className="sb2__label">{label}</span>
      {typeof badge === 'number' && badge > 0 && (
        <Badge pinned>{badge > 99 ? '99+' : String(badge)}</Badge>
      )}
      {badge === 'dot' && <Badge pinned dot variant="danger" />}
    </Link>
  );
}
